import { useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronUp, ChevronDown, ListTodo, StickyNote, Phone } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const STATUS_COLORS = {
  "Erstkontakt": "bg-zinc-700",
  "Termin geplant": "bg-blue-600",
  "Vor-Ort-Aufnahme": "bg-indigo-600",
  "Technische Prüfung": "bg-violet-600",
  "Angebot erstellt": "bg-amber-600",
  "Angebot versendet": "bg-orange-600",
  "Nachfassen": "bg-pink-600",
  "Gewonnen": "bg-[#00E575] text-[#09090B]",
  "Verloren": "bg-red-700",
  "Archiviert": "bg-zinc-600",
};

const FILTERS = [
  { key: "all", label: "Alle" },
  { key: "in_progress", label: "In Bearbeitung" },
  { key: "offers", label: "Angebote" },
  { key: "won", label: "Gewonnen" },
];

const matchesFilter = (p, key) => {
  if (key === "all") return true;
  if (key === "in_progress") return !["Gewonnen", "Verloren", "Archiviert"].includes(p.status);
  if (key === "offers") return ["Angebot erstellt", "Angebot versendet", "Nachfassen"].includes(p.status);
  if (key === "won") return p.status === "Gewonnen";
  return true;
};

const SortHeader = ({ label, sortKey, currentSort, onSort, className = "" }) => {
  const active = currentSort.key === sortKey;
  return (
    <button
      type="button"
      onClick={() => onSort(sortKey)}
      data-testid={`sort-${sortKey}`}
      className={`inline-flex items-center gap-0.5 font-mono text-[12px] uppercase tracking-wider transition ${active ? "text-[#00E575]" : "text-zinc-400 hover:text-zinc-200"} ${className}`}
    >
      {label}
      {active ? (currentSort.dir === "asc" ? <ChevronUp size={12} /> : <ChevronDown size={12} />) : <ChevronUp size={12} className="opacity-20" />}
    </button>
  );
};

const SwipeRow = ({ project, onTask, onNote, onCall, onOpen }) => {
  const [dx, setDx] = useState(0);
  const startX = useRef(null);
  const moved = useRef(false);

  const ACTION_WIDTH = 192; // 3 × 64

  const onTouchStart = (e) => {
    startX.current = e.touches[0].clientX;
    moved.current = false;
  };
  const onTouchMove = (e) => {
    if (startX.current == null) return;
    const d = e.touches[0].clientX - startX.current;
    if (Math.abs(d) > 6) moved.current = true;
    // Only allow left-swipe to reveal actions; clamp to action width.
    const clamped = Math.min(0, Math.max(-ACTION_WIDTH, d));
    setDx(clamped);
  };
  const onTouchEnd = () => {
    if (dx < -ACTION_WIDTH / 2) setDx(-ACTION_WIDTH);
    else setDx(0);
    startX.current = null;
  };

  const handleRowClick = (e) => {
    // Tap to open — only when not currently swiped open & no drag movement.
    if (dx !== 0) { setDx(0); return; }
    if (moved.current) return;
    onOpen(project);
  };

  return (
    <div className="relative overflow-hidden bg-[#18181B] border border-zinc-800 rounded-lg">
      {/* Action layer (revealed by swipe) */}
      <div className="absolute inset-y-0 right-0 flex" style={{ width: ACTION_WIDTH }}>
        <button
          type="button"
          data-testid={`swipe-task-${project.id}`}
          onClick={(e) => { e.stopPropagation(); onTask(project); setDx(0); }}
          className="w-16 h-full flex flex-col items-center justify-center gap-0.5 bg-blue-600 text-white text-[12px] font-semibold"
        >
          <ListTodo size={16} />
          Aufgabe
        </button>
        <button
          type="button"
          data-testid={`swipe-note-${project.id}`}
          onClick={(e) => { e.stopPropagation(); onNote(project); setDx(0); }}
          className="w-16 h-full flex flex-col items-center justify-center gap-0.5 bg-amber-600 text-white text-[12px] font-semibold"
        >
          <StickyNote size={16} />
          Notiz
        </button>
        <button
          type="button"
          data-testid={`swipe-call-${project.id}`}
          onClick={(e) => { e.stopPropagation(); onCall(project); setDx(0); }}
          className="w-16 h-full flex flex-col items-center justify-center gap-0.5 bg-[#00E575] text-[#09090B] text-[12px] font-bold"
        >
          <Phone size={16} />
          Anrufen
        </button>
      </div>

      {/* Foreground row */}
      <div
        data-testid={`project-row-${project.id}`}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onClick={handleRowClick}
        style={{ transform: `translateX(${dx}px)`, transition: dx === 0 || dx === -ACTION_WIDTH ? "transform 0.18s ease-out" : "none" }}
        className="relative bg-[#18181B] px-3 py-3 cursor-pointer hover:bg-[#1F1F23]"
      >
        {/* Mobile (default) / Tablet portrait: stacked card layout */}
        <div className="md:hidden flex flex-col gap-1.5">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <div className="font-medium text-[14px] leading-tight truncate">{project.customer_name || "—"}</div>
              <div className="text-[12px] font-mono text-zinc-300 truncate mt-0.5">{project.project_number}</div>
            </div>
            <span className={`shrink-0 px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider whitespace-nowrap ${STATUS_COLORS[project.status] || "bg-zinc-700"}`}>
              {project.status}
            </span>
          </div>
          <div className="flex items-center gap-3 text-[12px] text-zinc-400">
            <span className="font-mono text-zinc-300">PLZ {project.customer_zip || "—"}</span>
            <span className="text-zinc-600">·</span>
            <span className="truncate">{project.owner_name || "—"}</span>
          </div>
        </div>

        {/* Desktop md+ : original 4-column row */}
        <div className="hidden md:flex md:items-center md:gap-2">
          <div className="min-w-[120px] flex-1">
            <div className="font-medium text-[13px] truncate">{project.customer_name || "—"}</div>
            <div className="text-[12px] font-mono text-zinc-300 truncate">{project.project_number}</div>
          </div>
          <div className="w-[60px] font-mono text-[12px] text-zinc-300 text-center shrink-0">{project.customer_zip || "—"}</div>
          <div className="w-[110px] text-[12px] text-zinc-300 truncate shrink-0">{project.owner_name || "—"}</div>
          <div className="w-[140px] shrink-0 flex justify-end">
            <span className={`px-2 py-0.5 rounded text-[9px] font-medium uppercase tracking-wider whitespace-nowrap ${STATUS_COLORS[project.status] || "bg-zinc-700"}`}>
              {project.status}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export const ManagerProjectTable = ({ projects = [] }) => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState({ key: "created_at", dir: "desc" });

  const handleSort = (key) => {
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));
  };

  const visible = useMemo(() => {
    const filtered = projects.filter((p) => matchesFilter(p, filter));
    const dir = sort.dir === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const av = (a[sort.key] || "").toString().toLowerCase();
      const bv = (b[sort.key] || "").toString().toLowerCase();
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [projects, filter, sort]);

  const counts = useMemo(() => {
    const c = { all: projects.length, in_progress: 0, offers: 0, won: 0 };
    projects.forEach((p) => {
      if (matchesFilter(p, "in_progress")) c.in_progress++;
      if (matchesFilter(p, "offers")) c.offers++;
      if (matchesFilter(p, "won")) c.won++;
    });
    return c;
  }, [projects]);

  const onOpen = (p) => navigate(`/projects/${p.id}`);
  const onTask = (p) => navigate(`/projects/${p.id}?tab=tasks&new=1`);
  const onNote = (p) => navigate(`/projects/${p.id}?tab=messages`);
  const onCall = (p) => {
    if (p.customer_phone) {
      window.location.href = `tel:${p.customer_phone.replace(/\s+/g, "")}`;
    } else {
      toast.info("Keine Telefonnummer hinterlegt");
    }
  };

  return (
    <section data-testid="manager-project-table">
      {/* Quick filter pills */}
      <div className="flex items-center gap-1.5 mb-3 overflow-x-auto no-scrollbar -mx-4 px-4 pb-1">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            data-testid={`filter-${f.key}`}
            onClick={() => setFilter(f.key)}
            className={`shrink-0 h-8 px-3 rounded-full text-[12px] font-medium transition ${filter === f.key ? "bg-[#00E575] text-[#09090B]" : "bg-[#18181B] border border-zinc-700 text-zinc-300 hover:border-zinc-500"}`}
          >
            {f.label}
            <span className={`ml-1.5 text-[12px] font-mono ${filter === f.key ? "text-[#09090B]/70" : "text-zinc-300"}`}>{counts[f.key]}</span>
          </button>
        ))}
      </div>

      {/* Header row */}
      {/* Mobile: compact sort dropdown-like pills (avoid 4-col table header on narrow screens) */}
      <div className="md:hidden flex items-center gap-1.5 px-1 pb-2 text-[12px] text-zinc-400 overflow-x-auto no-scrollbar">
        <span className="font-mono uppercase tracking-wider text-zinc-500 shrink-0 mr-1">Sortieren:</span>
        <SortHeader label="Kunde" sortKey="customer_name" currentSort={sort} onSort={handleSort} />
        <SortHeader label="PLZ" sortKey="customer_zip" currentSort={sort} onSort={handleSort} />
        <SortHeader label="Vertrieb" sortKey="owner_name" currentSort={sort} onSort={handleSort} />
        <SortHeader label="Status" sortKey="status" currentSort={sort} onSort={handleSort} />
      </div>
      {/* Desktop: full table header */}
      <div className="hidden md:flex md:items-center md:gap-2 px-3 pb-2 border-b border-zinc-800">
        <div className="flex-1 min-w-[120px]">
          <SortHeader label="Kunde" sortKey="customer_name" currentSort={sort} onSort={handleSort} />
        </div>
        <div className="w-[60px] shrink-0 text-center">
          <SortHeader label="PLZ" sortKey="customer_zip" currentSort={sort} onSort={handleSort} />
        </div>
        <div className="w-[110px] shrink-0">
          <SortHeader label="Vertrieb" sortKey="owner_name" currentSort={sort} onSort={handleSort} />
        </div>
        <div className="w-[140px] shrink-0 flex justify-end">
          <SortHeader label="Status" sortKey="status" currentSort={sort} onSort={handleSort} />
        </div>
      </div>

      {visible.length === 0 ? (
        <div className="mt-3 bg-[#18181B] border border-zinc-800 rounded-xl p-6 text-center text-zinc-300 text-sm font-medium">
          Keine Projekte in dieser Ansicht.
        </div>
      ) : (
        <div className="space-y-1.5 mt-2">
          {visible.map((p) => (
            <SwipeRow
              key={p.id}
              project={p}
              onOpen={onOpen}
              onTask={onTask}
              onNote={onNote}
              onCall={onCall}
            />
          ))}
        </div>
      )}

      <p className="mt-3 text-[12px] text-zinc-300 italic px-1">
        Tipp: Zeile nach links wischen für Aufgabe / Notiz / Anrufen — Spaltenkopf zum Sortieren.
      </p>
    </section>
  );
};
